/**
 * Turn a swagger-client failure into something a guest can actually report.
 *
 * The backend answers with `{ code, message, details?, reference? }`. A 500 only
 * ever says "Internal server error", so without the reference (the Sentry event
 * id) a screenshot of the error is untraceable — which is exactly how the Aulani
 * payment failures came in. When the body isn't our JSON at all (a proxy 502, a
 * dropped connection) fall back to the status line instead of throwing, because
 * a throw inside the failure handler leaves the booking screen spinning forever.
 */

const parseBody = response => {
    if (!response) {
        return null;
    }
    if (response.obj && typeof response.obj === 'object') {
        return response.obj;
    }
    if (typeof response.body === 'object' && response.body !== null) {
        return response.body;
    }
    try {
        return JSON.parse(response.text);
    } catch (err) {
        return null;
    }
};

const describeDetails = details => {
    if (typeof details === 'string') {
        return details.trim() || null;
    }
    if (!details || typeof details !== 'object') {
        return null;
    }
    const parts = Object.entries(details)
        .filter(([, value]) => value !== null && value !== undefined && value !== '')
        .map(([key, value]) =>
            typeof value === 'object'
                ? `${key}: ${JSON.stringify(value)}`
                : `${key}: ${String(value)}`
        );
    return parts.length > 0 ? parts.join(', ') : null;
};

export const describeApiError = (err, fallback = 'Something went wrong, please try again.') => {
    const response = err && err.response;
    const body = parseBody(response);

    const rawMessage = body && typeof body.message === 'string' ? body.message.trim() : '';
    const message = rawMessage ? rawMessage.replace(/^\s*\w*Error:\s*/, '') : '';
    const details = describeDetails(body && body.details);
    const reference =
        body && typeof body.reference === 'string' && body.reference.trim()
            ? body.reference.trim()
            : null;

    const head =
        [message, details ? `(${details})` : null].filter(Boolean).join(' ') ||
        (response && response.status
            ? `Request failed (${response.status}${
                  response.statusText ? ` ${response.statusText}` : ''
              })`
            : (err && err.message) || fallback);

    return reference ? `${head} [ref: ${reference}]` : head;
};

export default describeApiError;
